import { type VariantProps, cva } from 'class-variance-authority';
import type { HTMLAttributes } from 'react';
import { cn } from '@/lib/utils';

/**
 * Status chip.
 *
 * Each variant is a tinted fill, a solid text colour, and a border one step
 * darker than the fill. The border is what stops a pale chip dissolving into a
 * white card — without it these read as coloured text rather than as objects,
 * which matters in a dense table where the chip IS the scannable signal.
 *
 * Semantic names (`success`, `warning`, …) sit alongside the palette names, so
 * existing callers keep working and new ones can say what they mean rather than
 * what colour they want.
 */
const badgeVariants = cva(
  'inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-semibold',
  {
    variants: {
      variant: {
        default: 'bg-status-navy-tint text-status-navy',
        secondary: 'bg-slate-100 text-slate-600',
        outline: 'border border-border text-foreground',
        green: 'bg-status-green-tint text-status-green',
        amber: 'bg-status-amber-tint text-[#9A3412]',
        red: 'bg-status-red-tint text-[#991B1B]',
        blue: 'bg-status-blue-tint text-primary',
        purple: 'bg-status-purple-tint text-primary',
        navy: 'bg-status-navy-tint text-status-navy',
        gray: 'bg-slate-100 text-slate-600',
        success: 'bg-status-green-tint text-status-green',
        warning: 'bg-status-amber-tint text-[#9A3412]',
        danger: 'bg-status-red-tint text-[#991B1B]',
        info: 'bg-status-blue-tint text-primary',
      },
    },
    defaultVariants: { variant: 'default' },
  },
);

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement>, VariantProps<typeof badgeVariants> {}

export function Badge({ className, variant, ...props }: BadgeProps) {
  return <span className={cn(badgeVariants({ variant }), className)} {...props} />;
}
